<template>
  <v-dialog
    v-model="show"
    width="90%"
    class="rounded-xl"
    @click:outside="close"
  >
    <v-card class="pa-5">
      <v-card-actions class="modal-header">
        <!-- Premium Users Chart -->
        <v-btn icon color="red" @click="close">
          <v-icon size="20">mdi-close</v-icon>
        </v-btn>
      </v-card-actions>
      <div class="content-container">
        <div class="calendar-container">
          <div v-if="selectedEventType !== null" class="total-events">
            <template v-if="selectedEventType === EVENT_TYPES.AVG_WORKOUT_TIME">
              {{ getEventTypeLabel(selectedEventType) }} (range avg): {{ getTotalEvents() }}
            </template>
            <template v-else>
              Total {{ getEventTypeLabel(selectedEventType) }}: {{ getTotalEvents() }}
            </template>
          </div>
          <select
            v-model="localSelectedEventType"
            class="event-type-select"
          >
            <option
              v-for="option in eventTypeOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.text }}
            </option>
          </select>

          <div class="similar-events-container" v-if="shouldShowSimilarEvents">
            <div style="flex: 1">
              <h4 class="similar-events-title">Similar Events:</h4>
              <div class="checkbox-group">
                <label
                  v-for="option in similarEventOptions"
                  :key="option.key"
                  class="checkbox-label"
                >
                  <input
                    type="checkbox"
                    :checked="similarEvents[option.key]"
                    class="similar-event-checkbox"
                    @change="handleSimilarEventChange(option.key, option.value)"
                  />
                  <span class="checkbox-label-text">
                    {{ option.label }}
                    <span
                      v-if="getSimilarEventTotal(option.key) > 0"
                      class="checkbox-total"
                    >
                      ({{ getSimilarEventTotal(option.key) }})
                    </span>
                  </span>
                </label>
              </div>
            </div>
            <div style="flex: 1" v-if="selectedEventType === EVENT_TYPES.WORKOUT_COMPLETED || selectedEventType === EVENT_TYPES.WORKOUT_STARTED || selectedEventType === EVENT_TYPES.WORKOUT_PAUSED || selectedEventType === EVENT_TYPES.WORKOUT_RESUMED">
              <h4 class="similar-events-title">Workout Type:</h4>
              <div class="checkbox-group">
                <label
                  v-for="option in workoutTypeOptions"
                  :key="option.key"
                  class="checkbox-label"
                >
                  <input
                    type="checkbox"
                    :checked="workoutTypes[option.key]"
                    class="similar-event-checkbox"
                    @change="handleWorkoutTypeChange(option.key, option.value)"
                  />
                  <span class="checkbox-label-text">
                    {{ option.label }}
                    <span
                      v-if="getWorkoutTypeTotal(option.key) > 0"
                      class="checkbox-total"
                    >
                      ({{ getWorkoutTypeTotal(option.key) }})
                    </span>
                  </span>
                </label>
              </div>
            </div>
          </div>

          <div class="similar-events-container" v-if="localSelectedEventType === EVENT_TYPES.ACTIVE_USERS">
            <div style="flex: 1">
              <h4 class="similar-events-title">User Type:</h4>
              <div class="checkbox-group">
                <label
                  v-for="option in userTypeOptions"
                  :key="option.key"
                  class="checkbox-label"
                >
                  <input
                    type="checkbox"
                    :checked="userTypes[option.key]"
                    class="similar-event-checkbox"
                    @change="handleUserTypeChange(option.key)"
                  />
                  <span class="checkbox-label-text">
                    {{ option.label }}
                    <span
                      v-if="getUserTypeTotal(option.key) > 0"
                      class="checkbox-total"
                    >
                      ({{ getUserTypeTotal(option.key) }})
                    </span>
                  </span>
                </label>
              </div>
            </div>
          </div>

          <v-date-picker
            v-model="localDates"
            range
            :min-date="new Date('03-10-2025')"
            :max-date="new Date()"
            :show-current="true"
            first-day-of-week="1"
            :allowed-dates="allowedDates"
            @change="onDateRangeChange"
          ></v-date-picker>
          <vue-json-to-csv
            class="download-icon-container"
            :csv-title="`premiumNumbers_${formatDateForCsv(chartData?.dateRange?.startDate)}-${formatDateForCsv(chartData?.dateRange?.endDate)}`"
            :json-data="chartData?.csvValues ?? []"
            ><v-icon class="download-icon-2" color="primary"
              >mdi-download-box-outline</v-icon
            >Download CSV
          </vue-json-to-csv>
        </div>
        <div class="big-chart">
          <canvas id="bigChart" />
          <div v-if="isLoading" class="chart-loader-overlay">
            <v-progress-circular
              indeterminate
              color="primary"
              size="64"
            ></v-progress-circular>
            <div class="loader-text">Loading chart...</div>
          </div>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import DateRangePicker from "vue2-daterange-picker";
//you need to import the CSS manually (in case you want to override it)
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";
import Chart from "chart.js";
import moment from "moment";
import { EVENT_TYPES } from "@/constants/eventTypes";
import { WORKOUT_TYPES } from "@/constants/workoutTypes";

export default {
  components: { DateRangePicker },
  name: "premium-chart-modal",
  model: {},
  props: {
    show: {
      type: Boolean,
      required: true,
      default: false,
    },
    chartData: {
      type: Object,
      required: true,
      default: [],
    },
    selectedEventType: {
      type: String,
      required: false,
      default: null,
    },
    dateRange: {
      type: Object,
      required: true,
      default: () => ({
        startDate: new Date(),
        endDate: new Date(),
      }),
    },
    similarEvents: {
      type: Object,
      required: true,
      default: () => ({
        workoutStarted: false,
        workoutPaused: false,
        workoutResumed: false,
        workoutCompleted: false,
        postCreated: false,
        postShared: false,
        postComment: false,
        postPump: false,
      }),
    },
    similarEventsData: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    workoutTypes: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    workoutTypesData: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    onUpdate: {
      type: Function,
      required: true,
      default: () => {},
    },
    onUpdateWorkoutType: {
      type: Function,
      required: true,
      default: () => {},
    },
    close: {
      type: Function,
      required: true,
      default: () => {},
    },
    isLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      currentChart: undefined,
      EVENT_TYPES,
      WORKOUT_TYPES,
      localDates: [
        moment().subtract(30, "days").format("YYYY-MM-DD"),
        moment().format("YYYY-MM-DD"),
      ],
      // Color palette for different chart lines
      chartColors: [
        { bg: "rgba(236, 92, 0, 0.2)", border: "rgba(236, 92, 0, 1)" }, // Orange (main)
        { bg: "rgba(0, 123, 255, 0.2)", border: "rgba(0, 123, 255, 1)" }, // Blue
        { bg: "rgba(40, 167, 69, 0.2)", border: "rgba(40, 167, 69, 1)" }, // Green
        { bg: "rgba(220, 53, 69, 0.2)", border: "rgba(220, 53, 69, 1)" }, // Red
        { bg: "rgba(111, 66, 193, 0.2)", border: "rgba(111, 66, 193, 1)" }, // Purple
      ],
      eventTypeOptions: [
        { text: "Premium Subscribers", value: null },
        ...Object.entries(EVENT_TYPES).map(([key, value]) => {
          const labelMap = {
            Login: "Login",
            SingUp: "Sign Up",
            "Workout created by user": "Workout Created",
            "Worout shared by user": "Workout Shared",
            "Workout started": "Workout Started",
            "Workout paused": "Workout Paused",
            "Workout resumend": "Workout Resumed",
            "Workout completed": "Workout Completed",
            "Post created": "Post Created",
            "Post shared": "Post Shared",
            "Post comment": "Post Comment",
            "Post pump": "Post Pump",
            "Subscription creadted": "Subscription Created",
            "Program join": "Program Join",
            "Program completed": "Program Completed",
            "IAChat message sent": "IAChat Message Sent",
            "Finished subscriptions": "Finished Subscriptions",
            "Active users": "Active Users",
            "Avg workout time": "Avg Workout Time",
          };
          return {
            text: labelMap[value] || value,
            value: value,
          };
        }),
      ],
      // Checkbox states for user types (Active Users)
      userTypes: {
        all: true,
        premium: false,
        free: false,
      },
    };
  },
  emits: ['eventTypeChange', 'dateRangeChange', 'similarEventsChange', 'workoutTypesChange'],
  computed: {
    localSelectedEventType: {
      get() {
        return this.selectedEventType;
      },
      set(newValue) {
        this.$emit('eventTypeChange', newValue);
      }
    },
    shouldShowSimilarEvents() {
      // Show similar events for workout-related and post-related events
      const relatedEvents = [
        // Workout events
        EVENT_TYPES.WORKOUT_STARTED,
        EVENT_TYPES.WORKOUT_PAUSED,
        EVENT_TYPES.WORKOUT_RESUMED,
        EVENT_TYPES.WORKOUT_COMPLETED,
        // Post events
        EVENT_TYPES.POST_CREATED,
        EVENT_TYPES.POST_SHARED,
        EVENT_TYPES.POST_COMMENT,
        EVENT_TYPES.POST_PUMP,
        // Finished subscriptions
        EVENT_TYPES.FINISHED_SUBSCRIPTIONS,
      ];
      return relatedEvents.includes(this.localSelectedEventType);
    },
    similarEventOptions() {
      if (!this.shouldShowSimilarEvents) return [];

      // Define workout events
      const workoutEvents = [
        {
          key: "workoutStarted",
          label: "Workout Started",
          value: EVENT_TYPES.WORKOUT_STARTED,
        },
        {
          key: "workoutPaused",
          label: "Workout Paused",
          value: EVENT_TYPES.WORKOUT_PAUSED,
        },
        {
          key: "workoutResumed",
          label: "Workout Resumed",
          value: EVENT_TYPES.WORKOUT_RESUMED,
        },
        {
          key: "workoutCompleted",
          label: "Workout Completed",
          value: EVENT_TYPES.WORKOUT_COMPLETED,
        },
      ];

      // Define post events
      const postEvents = [
        {
          key: "postCreated",
          label: "Post Created",
          value: EVENT_TYPES.POST_CREATED,
        },
        {
          key: "postShared",
          label: "Post Shared",
          value: EVENT_TYPES.POST_SHARED,
        },
        {
          key: "postComment",
          label: "Post Comment",
          value: EVENT_TYPES.POST_COMMENT,
        },
        {
          key: "postPump",
          label: "Post Pump",
          value: EVENT_TYPES.POST_PUMP,
        },
      ];

      // Define finished subscription events
      const finishedSubscriptionEvents = [
        {
          key: "finishedWeb",
          label: "Web",
          value: "finishedWebSubs",
        },
        {
          key: "finishedCoupons",
          label: "Coupons",
          value: "finishedCouponSubs",
        },
        {
          key: "finishedIap",
          label: "IAP",
          value: "finishedIAPSubs",
        },
      ];

      // Determine which group the selected event belongs to
      const workoutEventTypes = [
        EVENT_TYPES.WORKOUT_STARTED,
        EVENT_TYPES.WORKOUT_PAUSED,
        EVENT_TYPES.WORKOUT_RESUMED,
        EVENT_TYPES.WORKOUT_COMPLETED,
      ];

      const postEventTypes = [
        EVENT_TYPES.POST_CREATED,
        EVENT_TYPES.POST_SHARED,
        EVENT_TYPES.POST_COMMENT,
        EVENT_TYPES.POST_PUMP,
      ];

      let relevantEvents = [];
      if (workoutEventTypes.includes(this.localSelectedEventType)) {
        relevantEvents = workoutEvents;
      } else if (postEventTypes.includes(this.localSelectedEventType)) {
        relevantEvents = postEvents;
      } else if (this.localSelectedEventType === EVENT_TYPES.FINISHED_SUBSCRIPTIONS) {
        return finishedSubscriptionEvents;
      }

      // Filter out the currently selected event type
      return relevantEvents.filter(
        (event) => event.value !== this.localSelectedEventType
      );
    },
    similarEventsTotal() {
      // Calculate total events for all checked similar events
      let total = 0;
      Object.keys(this.similarEvents).forEach((key) => {
        if (this.similarEvents[key] && this.similarEventsData[key]) {
          const eventData = this.similarEventsData[key];
          if (eventData.yValues) {
            total += eventData.yValues.reduce((acc, curr) => acc + curr, 0);
          }
        }
      });
      return total;
    },
    workoutTypeOptions() {
      // Return workout type options for the dropdown
      return [
        {
          key: "coachedClass",
          label: "Coached Class",
          value: WORKOUT_TYPES.COACHED_CLASS,
        },
        {
          key: "live",
          label: "Live",
          value: WORKOUT_TYPES.LIVE,
        },
        {
          key: "preSet",
          label: "Pre-Set",
          value: WORKOUT_TYPES.PRE_SET,
        },
        {
          key: "freestyle",
          label: "Freestyle",
          value: WORKOUT_TYPES.FREESSTYLE,
        },
        {
          key: "byo",
          label: "BYO",
          value: WORKOUT_TYPES.BYO,
        },
      ];
    },
    userTypeOptions() {
      // Return user type options for Active Users
      return [
        {
          key: "all",
          label: "All Users",
        },
        {
          key: "premium",
          label: "Premium Users",
        },
        {
          key: "free",
          label: "Free Users",
        },
      ];
    },
  },
  mounted() {},
  methods: {
    getOrderedDates(dates) {
      const orderedDates = dates.sort((a, b) => new Date(a) - new Date(b));
      const startDate = orderedDates[0];
      const endDate = orderedDates[1];
      return { startDate, endDate };
    },
    getDatesRange(dates) {
      return [
        moment(dates.startDate).format("YYYY-MM-DD"),
        moment(dates.endDate).format("YYYY-MM-DD"),
      ];
    },
    createChart(chartData) {
      let premiumChart = document.getElementById("bigChart");
      if (this.currentChart) {
        this.currentChart.destroy();
      }

      // Check if any workout types are selected
      const hasWorkoutTypes = Object.keys(this.workoutTypes).some(
        (key) => this.workoutTypes[key]
      );

      // Check if Active Users is selected
      const isActiveUsers = this.localSelectedEventType === EVENT_TYPES.ACTIVE_USERS;
      const hasUserTypes = isActiveUsers && Object.keys(this.userTypes).some(
        (key) => this.userTypes[key]
      );

      // Create datasets array starting with main dataset (only if no workout types or user types are selected)
      const datasets = [];
      if (!hasWorkoutTypes && !hasUserTypes) {
        datasets.push({
          fill: true,
          label: this.getEventTypeLabel(this.selectedEventType) || "Premium users",
          data: chartData.yValues,
          backgroundColor: this.chartColors[0].bg,
          borderColor: this.chartColors[0].border,
          borderWidth: 1,
          tension: 0.1,
        });
      }

      // Handle user types for Active Users
      if (isActiveUsers && hasUserTypes) {
        if (this.userTypes.all && chartData.yValues) {
          datasets.push({
            fill: true,
            label: "All Users",
            data: chartData.yValues,
            backgroundColor: this.chartColors[0].bg,
            borderColor: this.chartColors[0].border,
            borderWidth: 1,
            tension: 0.1,
          });
        }
        if (this.userTypes.premium && chartData.premiumYValues) {
          datasets.push({
            fill: true,
            label: "Premium Users",
            data: chartData.premiumYValues,
            backgroundColor: this.chartColors[1].bg,
            borderColor: this.chartColors[1].border,
            borderWidth: 1,
            tension: 0.1,
          });
        }
        if (this.userTypes.free && chartData.freeYValues) {
          datasets.push({
            fill: true,
            label: "Free Users",
            data: chartData.freeYValues,
            backgroundColor: this.chartColors[2].bg,
            borderColor: this.chartColors[2].border,
            borderWidth: 1,
            tension: 0.1,
          });
        }
      }

      // Add similar events datasets (only if no workout types or user types are selected)
      if (
        !hasWorkoutTypes &&
        !hasUserTypes &&
        this.similarEventsData && 
        typeof this.similarEventsData === 'object'
      ) {
        Object.keys(this.similarEventsData).forEach((key, index) => {
          if (this.similarEvents[key] && this.similarEventsData[key]) {
            const eventData = this.similarEventsData[key];
            const colorIndex = (index + 1) % this.chartColors.length;
            const eventLabel =
              this.similarEventOptions.find((opt) => opt.key === key)?.label ||
              key;

            datasets.push({
              fill: true,
              label: eventLabel,
              data: eventData.yValues,
              backgroundColor: this.chartColors[colorIndex].bg,
              borderColor: this.chartColors[colorIndex].border,
              borderWidth: 1,
              tension: 0.1,
            });
          }
        });
      }

      // Add workout types datasets (only if workout types are selected and no user types)
      if (
        hasWorkoutTypes &&
        !hasUserTypes &&
        this.workoutTypesData && 
        typeof this.workoutTypesData === 'object'
      ) {
        Object.keys(this.workoutTypesData).forEach((key, index) => {
          if (this.workoutTypes[key] && this.workoutTypesData[key]) {
            const eventData = this.workoutTypesData[key];
            const colorIndex = (index + 1) % this.chartColors.length;
            const workoutTypeLabel = this.workoutTypeOptions.find(opt => opt.key === key)?.label || key;

            datasets.push({
              fill: true,
              label: workoutTypeLabel,
              data: eventData.yValues,
              backgroundColor: this.chartColors[colorIndex].bg,
              borderColor: this.chartColors[colorIndex].border,
              borderWidth: 1,
              tension: 0.1,
            });
          }
        });
      }

      this.currentChart = new Chart(premiumChart, {
        type: "line",
        data: {
          labels: chartData.xValues,
          datasets: datasets,
        },
        options: {
          responsive: true,
          legend: {
            display: datasets.length > 1,
            position: "top",
            labels: {
              usePointStyle: true,
              padding: 20,
              font: {
                size: 12,
              },
            },
          },
          scales: {
            yAxes: [
              {
                ticks: {
                  fontSize: 9,
                  callback: (val) => {
                    if (this.selectedEventType === EVENT_TYPES.AVG_WORKOUT_TIME) {
                      return Number(val).toFixed(1);
                    }
                    return Number(val).toFixed(0);
                  },
                },
              },
            ],
            xAxes: [
              {
                ticks: {
                  autoSkip: false,
                  maxRotation: 90,
                  minRotation: 90,
                  callback: function (val) {
                    return val;
                  },
                },
              },
            ],
          },
        },
      });
    },
    renderChart(chartValues) {
      setTimeout(() => {
        let premiumChart = document.getElementById("bigChart");
        if (chartValues && premiumChart) {
          this.createChart(chartValues);
        }
      }, 100);
    },
    allowedDates(date) {
      // Convert the date strings to Date objects for comparison
      const currentDate = new Date(date);
      const min = new Date("2025-03-10");
      const max = new Date();

      // Return true only for dates within the range (inclusive)
      return currentDate >= min && currentDate <= max;
    },
    onDateRangeChange(dates) {
      // Only emit when we have both start and end dates
      if (dates && dates.length === 2) {
        const orderedDates = this.getOrderedDates(dates);
        this.$emit('dateRangeChange', orderedDates);
      }
    },
    async handleEventTypeChange(value) {
      const newEventType = value.target.value === null || 
                          value.target.value === undefined || 
                          value.target.value === "" ? null : value.target.value;
      
      this.$emit('eventTypeChange', newEventType);
    },
    async handleSimilarEventChange(key, eventType) {
      const newSimilarEvents = { ...this.similarEvents };
      newSimilarEvents[key] = !newSimilarEvents[key];

      // If similar event is being checked, uncheck all workout types
      if (newSimilarEvents[key]) {
        const newWorkoutTypes = {
          coachedClass: false,
          live: false,
          preSet: false,
          freestyle: false,
          byo: false,
        };
        
        this.$emit('workoutTypesChange', {
          workoutTypes: newWorkoutTypes,
          workoutTypesData: {},
          changedKey: 'all',
          workoutType: null,
          isChecked: false,
        });
      }
      
      this.$emit('similarEventsChange', {
        similarEvents: newSimilarEvents,
        similarEventsData: this.similarEventsData,
        changedKey: key,
        eventType: eventType,
        isChecked: newSimilarEvents[key]
      });
      
      // If checking a similar event, fetch its data
      if (newSimilarEvents[key]) {
        await this.fetchSimilarEventData(key, eventType);
      }
    },
    async fetchSimilarEventData(key, eventType) {
      try {
        const startDate = moment(this.dateRange.startDate).startOf("day").toDate();
        const endDate = moment(this.dateRange.endDate).endOf("day").toDate();

        let res;
        // Check if this is a finished subscription event
        const finishedSubscriptionKeys = ['finishedWeb', 'finishedCoupons', 'finishedIap'];
        if (finishedSubscriptionKeys.includes(key)) {
          // For finished subscriptions, use the new action with collectionName
          res = await this.$store.dispatch("getFinishedSubscriptionsNumbers", {
            startDate,
            endDate,
            collectionName: eventType, // eventType will be the collection name (e.g., "finishedWebSubs")
          });
        } else {
          // For regular events, use the existing action
          res = await this.$store.dispatch("getEventsNumbers", {
            startDate,
            endDate,
            type: eventType,
          });
        }

        // Emit the new similar events data
        const newSimilarEventsData = { ...this.similarEventsData };
        newSimilarEventsData[key] = res;
        this.$emit('similarEventsChange', {
          similarEvents: this.similarEvents,
          similarEventsData: newSimilarEventsData,
          changedKey: key,
          eventType: eventType,
          isChecked: true
        });
      } catch (error) {
        console.error(`Error fetching data for ${eventType}:`, error);
        // Emit to uncheck the checkbox if there was an error
        const newSimilarEvents = { ...this.similarEvents };
        newSimilarEvents[key] = false;
        this.$emit('similarEventsChange', {
          similarEvents: newSimilarEvents,
          similarEventsData: this.similarEventsData,
          changedKey: key,
          eventType: eventType,
          isChecked: false
        });
      }
    },
    updateChart() {
      // Add a small delay to ensure data is properly updated
      setTimeout(() => {
        if (this.chartData && this.chartData.xValues && this.chartData.xValues.length > 0) {
          this.createChart(this.chartData);
        }
      }, 100);
    },
    getMainEventTotal() {
      if (this.chartData && this.chartData.yValues) {
        return this.chartData.yValues.reduce((acc, curr) => acc + curr, 0);
      }
      return 0;
    },
    getTotalEvents() {
      if (this.isLoading) return 0;
      // For FINISHED_SUBSCRIPTIONS, sum up all three subscription types
      if (this.selectedEventType === EVENT_TYPES.FINISHED_SUBSCRIPTIONS) {
        let total = 0;
        const subscriptionKeys = ['finishedWeb', 'finishedCoupons', 'finishedIap'];
        subscriptionKeys.forEach(key => {
          if (this.similarEventsData[key] && this.similarEventsData[key].yValues) {
            total += this.similarEventsData[key].yValues.reduce((acc, curr) => acc + curr, 0);
          }
        });
        return total;
      }
      // For ACTIVE_USERS, use total unique users based on selected user types
      if (this.selectedEventType === EVENT_TYPES.ACTIVE_USERS) {
        if (this.userTypes.all && this.chartData && this.chartData.totalUniqueUsers !== undefined) {
          return this.chartData.totalUniqueUsers;
        } else {
          let total = 0;
          if (this.userTypes.premium && this.chartData && this.chartData.totalUniquePremiumUsers !== undefined) {
            total += this.chartData.totalUniquePremiumUsers;
          }
          if (this.userTypes.free && this.chartData && this.chartData.totalUniqueFreeUsers !== undefined) {
            total += this.chartData.totalUniqueFreeUsers;
          }
          return total;
        }
      }
      // For Avg Workout Time, show average in minutes (e.g. "25.3 min")
      if (this.selectedEventType === EVENT_TYPES.AVG_WORKOUT_TIME) {
        const avg = this.chartData && this.chartData.totalEvents;
        return avg != null ? `${Number(avg).toFixed(1)} min` : "—";
      }
      // For other event types, use chartData totals
      return this.getMainEventTotal();
    },
    getEventTypeLabel(eventType) {
      if (!eventType) return "Premium Subscribers";
      const option = this.eventTypeOptions.find(
        (opt) => opt.value === eventType
      );
      return option ? option.text : eventType;
    },
    formatDateForCsv(val) {
      if (val == null) return "";
      if (typeof val.toLocaleDateString === "function") return val.toLocaleDateString();
      if (typeof val.toDate === "function") return val.toDate().toLocaleDateString();
      const d = new Date(val);
      return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
    },
    getSimilarEventTotal(key) {
      // Get total for a specific similar event
      if (this.similarEvents[key] && this.similarEventsData && this.similarEventsData[key]) {
        const eventData = this.similarEventsData[key];
        if (eventData.yValues) {
          const total = eventData.yValues.reduce((acc, curr) => acc + curr, 0);
          return total;
        }
      }
      return 0;
    },

    getWorkoutTypeTotal(key) {
      // Get total for a specific workout type
      if (this.workoutTypes[key] && this.workoutTypesData && this.workoutTypesData[key]) {
        const eventData = this.workoutTypesData[key];
        if (eventData.yValues) {
          const total = eventData.yValues.reduce((acc, curr) => acc + curr, 0);
          return total;
        }
      }
      return 0;
    },

    getUserTypeTotal(key) {
      // Get total unique users for a specific user type (Active Users)
      if (!this.chartData) return 0;
      
      if (key === "all" && this.chartData.totalUniqueUsers !== undefined) {
        return this.chartData.totalUniqueUsers;
      }
      if (key === "premium" && this.chartData.totalUniquePremiumUsers !== undefined) {
        return this.chartData.totalUniquePremiumUsers;
      }
      if (key === "free" && this.chartData.totalUniqueFreeUsers !== undefined) {
        return this.chartData.totalUniqueFreeUsers;
      }
      return 0;
    },

    handleUserTypeChange(key) {
      // Toggle the user type checkbox
      this.userTypes[key] = !this.userTypes[key];
      // Update the chart when user type selection changes
      this.updateChart();
    },

    async handleWorkoutTypeChange(key, workoutType) {

      const newWorkoutTypes = { ...this.workoutTypes };
      newWorkoutTypes[key] = !newWorkoutTypes[key];

      // If workout type is being checked, uncheck all similar events
      if (newWorkoutTypes[key]) {
        const newSimilarEvents = {
          workoutStarted: false,
          workoutPaused: false,
          workoutResumed: false,
          workoutCompleted: false,
          postCreated: false,
          postShared: false,
          postComment: false,
          postPump: false,
        };
        
        this.$emit('similarEventsChange', {
          similarEvents: newSimilarEvents,
          similarEventsData: {},
          changedKey: 'all',
          eventType: null,
          isChecked: false,
        });
      }

      this.$emit('workoutTypesChange', {
        workoutTypes: newWorkoutTypes,
        workoutTypesData: this.workoutTypesData,
        changedKey: key,
        workoutType: workoutType,
        isChecked: newWorkoutTypes[key],
      });

      if (newWorkoutTypes[key]) {
        // Checkbox is checked - fetch data for this workout type
        await this.fetchWorkoutTypeData(key, workoutType);
      } else {
        // Checkbox is unchecked - remove data using $delete for reactivity
        this.$delete(this.workoutTypesData, key);
      }
    },

    async fetchWorkoutTypeData(key, workoutType) {
      try {
        const startDate = moment(this.dateRange.startDate)
          .startOf("day")
          .toDate();
        const endDate = moment(this.dateRange.endDate)
          .endOf("day")
          .toDate();

        const res = await this.onUpdateWorkoutType({
          startDate,
          endDate,
          eventType: this.selectedEventType,
          workoutType: workoutType,
        });

        // Store the data for this workout type using $set for reactivity
        this.$set(this.workoutTypesData, key, res);
      } catch (error) {
        console.error(`Error fetching data for workout type ${workoutType}:`, error);
        // Uncheck the checkbox if there was an error
        const newWorkoutTypes = { ...this.workoutTypes };
        newWorkoutTypes[key] = false;
        this.$emit('workoutTypesChange', {
          workoutTypes: newWorkoutTypes,
          workoutTypesData: this.workoutTypesData,
          changedKey: key,
          workoutType: workoutType,
          isChecked: false,
        });
      }
    },
  },
  watch: {
    dateRange: {
      handler: function (newValue, oldValue) {
        if (newValue && newValue.startDate && newValue.endDate) {
          this.localDates = [
            moment(newValue.startDate).format("YYYY-MM-DD"),
            moment(newValue.endDate).format("YYYY-MM-DD"),
          ];
        }
      },
      deep: true,
      immediate: true,
    },
    chartData: {
      handler: function (newValue, oldValue) {
        this.renderChart(newValue);
      },
      deep: true,
      immediate: true,
    },
    selectedEventType: {
      handler: function (newValue, oldValue) {
        // When switching to FINISHED_SUBSCRIPTIONS, auto-check all subscription types
        if (newValue === EVENT_TYPES.FINISHED_SUBSCRIPTIONS && oldValue !== EVENT_TYPES.FINISHED_SUBSCRIPTIONS) {
          // The parent will handle auto-checking via handleModalEventTypeChange
          // This watcher is just to ensure UI stays in sync
        }
        // When switching to ACTIVE_USERS, default to showing "All Users"
        if (newValue === EVENT_TYPES.ACTIVE_USERS && oldValue !== EVENT_TYPES.ACTIVE_USERS) {
          this.userTypes.all = true;
          this.userTypes.premium = false;
          this.userTypes.free = false;
          this.updateChart();
        }
        // When switching away from ACTIVE_USERS, reset user types
        if (oldValue === EVENT_TYPES.ACTIVE_USERS && newValue !== EVENT_TYPES.ACTIVE_USERS) {
          this.userTypes.all = true;
          this.userTypes.premium = false;
          this.userTypes.free = false;
        }
      },
      immediate: false,
    },
  },
};
</script>

<style scoped>
.modal-header {
  justify-content: flex-end;
  font-size: 18px;
  color: #3a3a3a;
  font-weight: bold;
}
.big-chart {
  flex: 1;
  position: relative;
}

.chart-loader-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 4px;
}

.loader-text {
  margin-top: 16px;
  font-size: 14px;
  color: #3a3a3a;
  font-weight: 500;
}
.content-container {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 32px;
}
.calendar-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
}
.download-icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  font-weight: bold;
  color: #3a3a3a;
  transition: all 0.2s ease-in-out;
  opacity: 0.8;
}
.download-icon-2 {
  font-size: 32px;
  margin-right: 4px;
}
.download-icon-container:hover {
  cursor: pointer;
  transform: scale(1.1);
  opacity: 1;
}

.total-events {
  font-weight: bold;
  font-size: 16px;
  color: #3a3a3a;
  text-align: left;
}

.similar-events-total {
  font-weight: bold;
  font-size: 14px;
  color: #ec5c00;
  margin-bottom: 12px;
  padding: 4px 8px;
  background-color: rgba(236, 92, 0, 0.1);
  border-radius: 4px;
  border-left: 3px solid #ec5c00;
  text-align: center;
}

.event-type-select {
  width: 100%;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #3a3a3a;
  padding: 4px 8px;
  font-size: 14px;
  text-align: center;
}

.event-type-select:hover {
  cursor: pointer;
}

.similar-events-container {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-direction: row;
  width: 100%;
  margin-bottom: 16px;
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #f9f9f9;
  gap: 16px;
}

.similar-events-title {
  font-weight: bold;
  font-size: 14px;
  color: #3a3a3a;
  margin: 0 0 8px 0;
}

.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}

.checkbox-label {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #3a3a3a;
  cursor: pointer;
  user-select: none;
}

.checkbox-label-text {
  display: flex;
  align-items: center;
  gap: 4px;
}

.checkbox-total {
  font-weight: bold;
  color: #ec5c00;
  font-size: 11px;
}

.similar-event-checkbox {
  margin-right: 6px;
  transform: scale(1.1);
  cursor: pointer;
}

.checkbox-label:hover {
  color: #ec5c00;
}
</style>
